
import React, { useState, useEffect } from 'react';

export const DNA_DATA = {
  bodyProportions: {
    title: "Body Proportions",
    text: "While height and weight tell us how large a person is, body proportions map out how that mass is distributed vertically and horizontally. Professional stylists evaluate these proportions to understand where garments should sit, taper, or flare to create optical balance.",
    sections: [
      {
        label: "Vertical Proportions (The Head System)",
        content: "The standard unit of measurement is the length of the head. Most people are naturally 7 to 7.5 heads tall. We look at the balance between the torso and lower limbs: Balanced (torso=legs), Short Torso (high-waisted), or Long Torso (low-waisted)."
      },
      {
        label: "Horizontal Proportions (Silhouette)",
        content: "Determined by comparing the width of shoulders, waist, and hips. Classic shapes include Hourglass (balanced shoulders/hips), Inverted Triangle (wide shoulders), Rectangle (equal width), Triangle (Pear), and Round (Apple)."
      }
    ]
  },
  boneStructure: {
    title: "Bone Structure",
    text: "Bone structure refers to the underlying skeletal framework of the body. It is the permanent, unchangeable foundation that determines how clothing drapes over you.",
    sections: [
      {
        label: "Frame Size (Skeletal Volume)",
        content: "Refers to the thickness and scale of bones relative to height. Petite/Small (fine bones), Medium (balanced mass), or Large/Broad (sturdy/prominent bones)."
      },
      {
        label: "Skeletal Type (Yin vs Yang)",
        content: "Yang (Angular/Sharp) features long, linear lines and square jawlines. Yin (Rounded/Soft) features curved, smooth lines and rounded shoulders."
      }
    ]
  },
  age: {
    title: "Age Range",
    text: "AI estimates your developmental stage to optimize garment cuts. Professional styling for a 20-year-old varies significantly from a 50-year-old, focusing on fabric density and silhouette maturity.",
    sections: []
  },
  gender: {
    title: "Gender Identity",
    text: "Used to determine primary sizing blocks and garment construction logic (Menswear vs Womenswear vs Fluid).",
    sections: []
  },
  skinUndertone: {
    title: "Skin Undertone",
    text: "Your skin tone is the surface color, but your undertone is the subtle color beneath it. It stays identical your entire life.",
    sections: [
      {
        label: "Categories",
        content: "Cool (pink/blue base), Warm (yellow/golden base), Neutral (balanced), or Olive (explicit green/yellow cast)."
      }
    ]
  },
  hairColor: {
    title: "Hair Level & Tone",
    text: "Hair color is defined by depth (Level) and warmth/coolness (Tone).",
    sections: [
      {
        label: "The Level System",
        content: "Ranges from Level 1 (Pure Black) to Level 10 (Lightest Platinum Blonde), determined by Eumelanin concentration."
      },
      {
        label: "The Tone System",
        content: "Warm (gold/copper), Cool (ash/blue/violet), or Neutral (balanced chocolate/beige shades)."
      }
    ]
  },
  eyeColor: {
    title: "Eye Color & Contrast",
    text: "Eye color is determined by the melanin concentration in the iris and light scattering.",
    sections: [
      {
        label: "Intensity",
        content: "Deep Brown/Black (high melanin), Blue/Gray (zero stroma melanin, high scattering), Green (low melanin + scattering), or Hazel/Amber (uneven melanin distribution)."
      }
    ]
  }
};

interface DNAInfoProps {
  type: keyof typeof DNA_DATA;
}

const DNAInfo: React.FC<DNAInfoProps> = ({ type }) => {
  const [show, setShow] = useState(false);
  const data = DNA_DATA[type];

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShow(false);
    };
    if (show) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [show]);

  return (
    <div className="relative inline-block ml-2">
      <button 
        onClick={(e) => {
            e.stopPropagation();
            setShow(!show);
        }}
        className={`w-4 h-4 rounded-full border border-azure/40 text-azure-400 text-[10px] font-bold flex items-center justify-center transition-all
            ${show ? 'bg-azure text-dk-900 border-azure shadow-lg shadow-azure/20 scale-110' : 'hover:bg-azure/10'}
        `}
      >
        i
      </button>
      
      {show && (
        <>
            {/* Transparent Backdrop to capture "click anywhere to close" */}
            <div 
                className="fixed inset-0 z-[400] bg-black/5 backdrop-blur-[2px]" 
                onClick={() => setShow(false)}
            />

            <div 
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md p-6 bg-dk-800 border border-azure/30 rounded-[2rem] shadow-[0_0_50px_rgba(59,130,246,0.2)] z-[500] animate-zoom-in"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-4 border-b border-white/5 pb-3">
                    <div>
                        <span className="text-azure-400 text-[8px] font-black uppercase tracking-[0.3em] block mb-1">Technical Guidance</span>
                        <h4 className="text-sm font-black text-white uppercase tracking-widest">{data.title}</h4>
                    </div>
                    <button onClick={() => setShow(false)} className="text-gray-500 hover:text-white transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
                    <p className="text-[11px] text-gray-300 leading-relaxed font-medium">{data.text}</p>
                    
                    {data.sections.map((s, i) => (
                        <div key={i} className="p-4 bg-white/[0.03] rounded-2xl border border-white/5 space-y-1">
                            <p className="text-[10px] font-black text-azure-400 uppercase tracking-tighter">{s.label}</p>
                            <p className="text-[10px] text-gray-400 leading-normal italic">{s.content}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-6 pt-4 border-t border-white/5 text-center">
                    <button 
                        onClick={() => setShow(false)}
                        className="text-[9px] font-black text-gray-500 hover:text-azure-400 uppercase tracking-[0.3em] transition-colors"
                    >
                        [ Acknowledge Data ]
                    </button>
                </div>
            </div>
        </>
      )}
    </div>
  );
};

export default DNAInfo;
